import { FormattedMessage, useIntl } from 'react-intl';
import { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Search } from 'lucide';

//MUI
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Box from '@mui/system/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// common
import Icon from 'common/components/Icon';
import usePaginationHook from 'common/hook/usePaginationHook';
import useLocalStorage from 'common/hook/useLocalStorage';
import { AppState } from '@redux/store';
import { isClientOrgType } from 'enum/combobox';
import { NotFound } from 'svg';

// screen
import FilterBox from 'modules/suppliers/list/components/FilterBox';
import { SupplierFilterModel } from 'modules/suppliers/types';
import SupplierTable from 'modules/suppliers/list/components/SupplierTable';
import useProcessingFoodSuppliers, {
  type Supplier,
} from 'modules/suppliers/services/useProcessingFoodSuppliers';
import useUnprocessedDishSuppliers from 'modules/suppliers/services/useUnprocessedDishSupplier';
import useGetListClients from 'modules/account/services/useGetListClients';
import { Client } from 'modules/account/types';

const normalize = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const SelectCustomer = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        color: 'text.secondary',
        fontSize: '1.1rem',
      }}
    >
      <Box>
        <Typography sx={{ textAlign: 'center' }}>
          <NotFound />
        </Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          <FormattedMessage id="supplier.noSelectedClient" />
        </Typography>
        
      </Box>
    </Box>
  );
};

const SupplierListPage = () => {
  const hookPagination = usePaginationHook<SupplierFilterModel>({
    defaultFilter: { sort: 'commodityGroup' } as Partial<SupplierFilterModel>,
  });
  const { params, sorting, onSortingChange, onPageChange, onRowsPerPageChange, tab } =
    hookPagination;

  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState<number>(0);

  const [selectedClient, setSelectedClient] = useLocalStorage<Client | null>(
    'supplier-selected-client',
    null
  );

  // HOTFIX: tạm đổi type 2 -> 1. `type=2` là "danh sách khách hàng nội bộ" của KIOS (cùng business
  // rule getForInternalFsolApi) — tập này hẹp hơn hẳn, nhiều khách hàng thật không nằm trong đó nên
  // dropdown ra rỗng. `type=1` (thực chất là KHÔNG lọc) để prod lấy được khách hàng đã.
  // Cần chốt lại với KIOS xem đúng ra phải dùng tập nào.
  const { data: clients = [] } = useGetListClients({ type: 1 });

  const PROCESSED_FOOD_TAB_TEXT = 'thuc-pham-che-bien';
  const UNPROCESSED_DISH_TAB_TEXT = 'mon-khong-che-bien';

  const textToTabIndex = (tabText: string) => {
    return tabText === UNPROCESSED_DISH_TAB_TEXT ? 1 : 0;
  };

  // Sync tab with URL parameters
  const tabFromUrl = tab ? textToTabIndex(tab) : 0;

  // Update local state when URL changes
  useEffect(() => {
    setTabValue(tabFromUrl);
  }, [tabFromUrl]);

  const queries = {
    pageSize: params.pageSize,
    offset: (params.pageIndex - 1) * params.pageSize,
    customerOperationCode: params.filters.customerOperationCode,
    status: params.filters.status,
    sort: params.sort || 'commodityGroup',
  };

  // Fetch full lists once (large pageSize) — paginate & filter client-side
  const fullListQueries = {
    customerOperationCode: params.filters.customerOperationCode,
    status: params.filters.status,
    pageSize: 1000,
    offset: 0,
    sort: queries.sort,
  };
  const {
    data: allProcessedFood,
    isLoading: processedFoodSuppliersLoading,
    refetch: refetchProcessed,
  } = useProcessingFoodSuppliers({ queries: fullListQueries });
  const {
    data: allPackedDish,
    isLoading: unProcessedDishSuppliersLoading,
    refetch: refetchPacked,
  } = useUnprocessedDishSuppliers({ queries: fullListQueries });

  const [search, setSearch] = useState('');
  const searchQuery = normalize(search);

  const filterByName = (rows: Supplier[]) => {
    if (!searchQuery) return rows;
    return rows.filter((r) => normalize(r.name || '').includes(searchQuery));
  };

  const filteredProcessed = useMemo(
    () => filterByName(allProcessedFood?.results ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allProcessedFood, searchQuery]
  );
  const filteredPacked = useMemo(
    () => filterByName(allPackedDish?.results ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allPackedDish, searchQuery]
  );

  const paginate = (rows: Supplier[]) => {
    const start = (params.pageIndex - 1) * params.pageSize;
    return rows.slice(start, start + params.pageSize);
  };
  const pagedProcessed = paginate(filteredProcessed);
  const pagedPacked = paginate(filteredPacked);

  // Combined suggestion list for the autocomplete dropdown
  const suggestionNames = useMemo(() => {
    const set = new Set<string>();
    [...(allProcessedFood?.results ?? []), ...(allPackedDish?.results ?? [])].forEach((s) => {
      if (s.name) set.add(s.name);
    });
    return Array.from(set);
  }, [allProcessedFood, allPackedDish]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (params.pageIndex !== 1) {
      hookPagination.onPageChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const { formatMessage } = useIntl();

  const selectedClientName = selectedClient?.name || '';

  const tabIndexToText = (tab: number) => {
    return tab === 0 ? PROCESSED_FOOD_TAB_TEXT : UNPROCESSED_DISH_TAB_TEXT;
  };

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    if (!params.filters?.customerOperationCode) return;
    setTabValue(newValue);
    // Update URL with new tab value
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('tab', tabIndexToText(newValue));
    setSearchParams(currentParams, { replace: true });
  };

  const tableProps = {
    sorting,
    onSortingChange,
    onPageChange,
    onRowsPerPageChange,
  };

  const hideColumns = params.filters?.status === 'paused';

  const tabLabels = [
    {
      intlLabel: 'supplier.category.processedFood',
      itemsCount: filteredProcessed.length,
    },
    {
      intlLabel: 'supplier.category.unprocessedDish',
      itemsCount: filteredPacked.length,
    },
  ];

  const tabPanels = [
    <SupplierTable
      isLoading={processedFoodSuppliersLoading}
      params={params}
      suppliers={pagedProcessed}
      totalCount={filteredProcessed.length}
      tableProps={tableProps}
      tab={tabIndexToText(tabValue)}
      hideColumns={hideColumns}
    />,
    <SupplierTable
      isLoading={unProcessedDishSuppliersLoading}
      params={params}
      suppliers={pagedPacked}
      totalCount={filteredPacked.length}
      tableProps={tableProps}
      tab={tabIndexToText(tabValue)}
      hideColumns={hideColumns}
    />,
  ];

  // Cùng cách mở màn với Dashboard: khách hàng/phụ huynh khóa đúng trường của họ;
  // tài khoản chọn được nhiều trường thì mở màn là có danh sách, không chờ bấm Tìm kiếm.
  const userAuthen = useSelector((s: AppState) => s.auth.userInfo?.userAuthen);
  const isClientPersona = isClientOrgType(userAuthen?.userActualManagingOrgType);
  const myOrgId = userAuthen?.userActualManagingOrgId;
  const hasAutoAppliedRef = useRef(false);

  const applyClient = (client: Client, status?: SupplierFilterModel['status']) => {
    setSelectedClient(client);
    hookPagination.setParams({
      customerOperationCode: client.operationCode,
      status: status || params.filters.status || 'available',
    });
  };

  useEffect(() => {
    if (!isClientPersona || clients.length === 0) return;
    const mine = clients.find((c) => c.operationCode === myOrgId) ?? clients[0];
    if (!mine) return;
    if (params.filters.customerOperationCode !== mine.operationCode) {
      applyClient(mine);
    } else if (mine.operationCode !== selectedClient?.operationCode) {
      setSelectedClient(mine);
    }
    hasAutoAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientPersona, clients, myOrgId, params.filters.customerOperationCode]);

  useEffect(() => {
    if (isClientPersona || !userAuthen) return;
    if (hasAutoAppliedRef.current) return;
    if (clients.length === 0) return;
    // Quay lại từ chi tiết (URL đã có khách) thì giữ bộ lọc, không nhảy về trường đầu danh sách.
    if (params.filters.customerOperationCode) {
      hasAutoAppliedRef.current = true;
      return;
    }
    applyClient(clients[0]);
    hasAutoAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientPersona, userAuthen, clients, params.filters.customerOperationCode]);

  useEffect(() => {
    if (params.filters?.customerOperationCode && clients.length > 0) {
      if (selectedClient?.operationCode !== params.filters.customerOperationCode) {
        const client = clients.find(
          (c) => c.operationCode === params.filters.customerOperationCode
        );
        if (client) setSelectedClient(client);
      }
      return;
    }
    // Làm mới xóa trắng: chỉ gỡ khách sau khi đã tự áp một lần, không xóa trước lúc mở màn.
    if (hasAutoAppliedRef.current && !params.filters?.customerOperationCode && selectedClient) {
      setSelectedClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.filters?.customerOperationCode, clients]);

  const handleReset = () => {
    if (isClientPersona) {
      refetchProcessed();
      refetchPacked();
      return;
    }
    hookPagination.clearParams();
    setSelectedClient(null);
  };

  return (
    <Paper className="paper-list">
      <Box className="paper-filter">
        <FilterBox
          key={selectedClient?.operationCode || 'no-client'}
          hookPagination={hookPagination}
          selectedClient={selectedClient}
          disableClientSelect={isClientPersona}
          onReset={handleReset}
          onSetCustomer={(val) => {
            if (!val?.operationCode) {
              setSelectedClient(null);
              return;
            }
            applyClient(val);
          }}
        />
      </Box>

      {!params.filters?.customerOperationCode ? (
        <SelectCustomer />
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              pl: 2,
              pr: 2,
            }}
          >
            <Typography>
              <FormattedMessage id="supplier.suppliersServingCustomer" />
              {': '}
              {selectedClientName}
            </Typography>
            <Autocomplete<string, false, false, true>
              freeSolo
              options={suggestionNames}
              inputValue={search}
              onInputChange={(_, value) => setSearch(value)}
              sx={{ width: 320 }}
              filterOptions={(opts, state) => {
                const q = normalize(state.inputValue);
                if (!q) return opts;
                return opts.filter((name) => normalize(name).includes(q));
              }}
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder={formatMessage({ id: 'supplier.searchSupplier' })}
                  InputProps={{
                    ...p.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon={Search} size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
          {/* tabs  */}
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            scrollButtons="auto"
            aria-label="basic tabs example"
          >
            {tabLabels.map((item, index) => {
              return (
                <Tab
                  key={index}
                  label={
                    <Typography>
                      <FormattedMessage id={item.intlLabel} />
                      <Typography
                        component={'span'}
                        sx={{
                          margin: 1,
                          bgcolor: 'success.main',
                          borderRadius: '50%',
                          height: '24px',
                          aspectRatio: '1 / 1',
                          color: '#fff',
                          fontSize: '0.875rem',
                          padding: '4px 8px',
                        }}
                      >
                        {item.itemsCount}
                      </Typography>
                    </Typography>
                  }
                  id={`simple-tab-${index}`}
                  aria-controls={`simple-tabpanel-${index}`}
                  sx={{
                    '&.Mui-selected': {
                      color: 'primary.main',
                      '& .MuiTypography-root': {
                        color: 'primary.main', // ensure nested Typography also gets the color
                        '& span': {
                          color: '#fff', // keep the count badge text white
                        },
                      },
                    },
                  }}
                />
              );
            })}
          </Tabs>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />
          {tabPanels.map((panel, index) => {
            if (tabValue !== index) return null;
            return <Fragment key={index}>{panel}</Fragment>;
          })}
        </>
      )}
    </Paper>
  );
};

export default SupplierListPage;
